import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { clientsAPI, filesAPI, clientDocumentsAPI, carePlansAPI, visitsAPI, assignmentsAPI, aiAPI, clientEmailAPI } from '../services/api';
import { US_TIMEZONES, tzAbbrev } from '../services/timezone';
import FormSubmissions from '../components/FormSubmissions';
import ClientBilling from '../components/ClientBilling';
import ClientScheduleTab from '../components/ClientScheduleTab';
import IncidentReportsTab from '../components/IncidentReportsTab';
import CareNotesFeed from '../components/CareNotesFeed';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  Users,
  ArrowLeft,
  Phone,
  MapPin,
  FirstAid,
  Warning,
  Calendar,
  UploadSimple,
  File as FileIcon,
  FilePdf,
  FileImage,
  FileDoc,
  Trash,
  DownloadSimple,
  Plus,
  PencilSimple,
  ClipboardText,
  Note,
  Eye,
  ShieldCheck,
  EnvelopeSimple
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// Preset activity library — grouped by category. Care plan creator ticks which apply.
const ACTIVITY_LIBRARY = [
  {
    category: 'Personal Care',
    items: [
      'Shower or Bath',
      'Bed Bath',
      'Chair Bath',
      'Hair and Personal Grooming',
      'Shampoo and Scalp Care',
      'Hair Combing / Styling',
      'Oral Hygiene / Dental Care',
      'Dressing Assistance',
      'Shaving',
      'Skin Care Routine',
      'Apply Lotion / Moisturizer'
    ]
  },
  {
    category: 'Mobility & Comfort',
    items: [
      'Mobility & Ambulation Assistance',
      'Chair-to-bed Transfer',
      'Commode & Hygiene Maintenance',
      'Mobility Aid Support',
      'Repositioning',
      'Range of Motion',
      'Care of Medical Equipment',
      'Bedside Assistance'
    ]
  },
  {
    category: 'Health Services',
    items: [
      'Personal Hygiene',
      'Support with Catheter Care',
      'Clean Drainage Bag',
      'Medication Reminders',
      'Record Bowel Movements',
      'Assist with Walking Aids',
      'Provide Incontinence Support'
    ]
  },
  {
    category: 'Meal & Nutrition',
    items: [
      'Meal Preparation and Feeding',
      'Arrange Meal Area',
      'Provide Oral Supplements'
    ]
  },
  {
    category: 'Housekeeping and Home Support',
    items: [
      'Change Linens & Refresh Bedding',
      'Make Bed Neatly',
      'Do Laundry / Fold / Iron Clothing',
      'Grocery or Personal Shopping',
      'Clean Floors & Surfaces',
      'Kitchen Cleaning & Organization',
      'Wash Dishes & Clean Counters',
      'Take Out Trash & Replace Liners',
      'Bedroom Cleaning & Freshening',
      'Bathroom Cleaning & Sanitizing',
      'Provide General Supervision'
    ]
  }
];

const ClientProfilePage = () => {
  const { clientId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [client, setClient] = useState(null);
  const [files, setFiles] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [carePlans, setCarePlans] = useState([]);
  const [visits, setVisits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Document form state
  const [isDocCreateOpen, setIsDocCreateOpen] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [docForm, setDocForm] = useState({
    title: '',
    doc_type: 'note',
    content: ''
  });

  // Care plan form state (structured tasks)
  const [isCarePlanOpen, setIsCarePlanOpen] = useState(false);
  const [editingCarePlanId, setEditingCarePlanId] = useState(null);
  const emptyCarePlanForm = () => ({
    title: '',
    description: '',
    goals: '',
    frequency: '',
    tasks: [] // [{name, notes, required, days_of_week:[]}]
  });
  const [carePlanForm, setCarePlanForm] = useState(emptyCarePlanForm());
  const [carePlanSaving, setCarePlanSaving] = useState(false);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(false);
  const [welcomeEmail, setWelcomeEmail] = useState('');
  const [welcomeMsg, setWelcomeMsg] = useState('');
  const [welcomeSending, setWelcomeSending] = useState(false);
  const [isTzOpen, setIsTzOpen] = useState(false);
  const [tzDraft, setTzDraft] = useState('America/New_York');
  const [tzSaving, setTzSaving] = useState(false);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  const openEditProfile = () => {
    setEditForm({
      name: client?.name || '',
      date_of_birth: client?.date_of_birth || '',
      start_date: client?.start_date || '',
      address: client?.address || '',
      email: client?.email || '',
      emergency_contact: client?.emergency_contact || '',
      emergency_phone: client?.emergency_phone || '',
      emergency_email: client?.emergency_email || '',
      medical_conditions: (client?.medical_conditions || []).join(', '),
      allergies: (client?.allergies || []).join(', '),
      notes: client?.notes || ''
    });
    setIsEditOpen(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { toast.error('Name is required'); return; }
    setEditSaving(true);
    try {
      await clientsAPI.update(clientId, {
        user_id: client.user_id || 'none',
        name: editForm.name,
        date_of_birth: editForm.date_of_birth || null,
        start_date: editForm.start_date || null,
        address: editForm.address || null,
        email: editForm.email || null,
        emergency_contact: editForm.emergency_contact || null,
        emergency_phone: editForm.emergency_phone || null,
        emergency_email: editForm.emergency_email || null,
        medical_conditions: editForm.medical_conditions.split(',').map(s => s.trim()).filter(Boolean),
        allergies: editForm.allergies.split(',').map(s => s.trim()).filter(Boolean),
        notes: editForm.notes || null
      });
      toast.success('Client information updated');
      setIsEditOpen(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update client');
    } finally { setEditSaving(false); }
  };

  const openTimezone = () => {
    setTzDraft(client?.timezone || 'America/New_York');
    setIsTzOpen(true);
  };

  const handleSaveTimezone = async () => {
    if (!client) return;
    setTzSaving(true);
    try {
      // Backend uses ClientProfileCreate (full body) — send the full editable payload
      await clientsAPI.update(clientId, {
        user_id: client.user_id,
        name: client.name,
        date_of_birth: client.date_of_birth || null,
        address: client.address || null,
        email: client.email || null,
        emergency_contact: client.emergency_contact || null,
        emergency_phone: client.emergency_phone || null,
        emergency_email: client.emergency_email || null,
        medical_conditions: client.medical_conditions || [],
        allergies: client.allergies || [],
        notes: client.notes || null,
        standard_rate: client.standard_rate ?? null,
        overtime_rate: client.overtime_rate ?? null,
        weekend_rate: client.weekend_rate ?? null,
        timezone: tzDraft,
        start_date: client.start_date || null,
      });
      toast.success('Timezone updated');
      setIsTzOpen(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update timezone');
    } finally { setTzSaving(false); }
  };

  const openWelcome = () => {
    setWelcomeEmail(client?.email || client?.emergency_email || '');
    setWelcomeMsg('');
    setIsWelcomeOpen(true);
  };

  const handleSendWelcome = async () => {
    if (!welcomeEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(welcomeEmail.trim())) {
      toast.error('Please enter a valid email address');
      return;
    }
    setWelcomeSending(true);
    try {
      const res = await clientEmailAPI.sendWelcome(clientId, {
        recipient_email: welcomeEmail.trim(),
        custom_message: welcomeMsg.trim() || null,
      });
      toast.success(`Welcome email sent to ${res.data.recipient}`);
      setIsWelcomeOpen(false);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send welcome email');
    } finally { setWelcomeSending(false); }
  };

  useEffect(() => {
    loadAllData();
  }, [clientId]);

  const [convertingLead, setConvertingLead] = useState(false);
  const handleConvertToActive = async () => {
    setConvertingLead(true);
    try {
      await clientsAPI.convertToActive(clientId);
      toast.success(`${client?.name} is now an active client`);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert lead');
    } finally { setConvertingLead(false); }
  };

  const loadAllData = async () => {
    try {
      const [clientRes, filesRes, docsRes, plansRes, visitsRes, assignRes] = await Promise.all([
        clientsAPI.getById(clientId),
        filesAPI.list(clientId),
        clientDocumentsAPI.list(clientId),
        carePlansAPI.getAll(clientId),
        visitsAPI.getAll({ client_id: clientId }),
        assignmentsAPI.getAll({ client_id: clientId })
      ]);
      setClient(clientRes.data);
      setFiles(filesRes.data);
      setDocuments(docsRes.data);
      setCarePlans(plansRes.data);
      setVisits(visitsRes.data);
      setAssignments(assignRes.data);
    } catch (error) {
      console.error('Error loading client profile:', error);
      toast.error('Failed to load client profile');
    } finally {
      setLoading(false);
    }
  };

  const openCreateCarePlan = () => {
    setEditingCarePlanId(null);
    setCarePlanForm(emptyCarePlanForm());
    setIsCarePlanOpen(true);
  };

  const openEditCarePlan = (plan) => {
    setEditingCarePlanId(plan.id);
    // Prefer structured tasks; fall back to legacy activities strings
    const tasks = Array.isArray(plan.tasks) && plan.tasks.length > 0
      ? plan.tasks.map(t => ({
          name: t.name || '',
          notes: t.notes || '',
          required: !!t.required,
          days_of_week: Array.isArray(t.days_of_week) ? t.days_of_week : []
        }))
      : (plan.activities || []).map(a => ({ name: a, notes: '', required: false, days_of_week: [] }));
    setCarePlanForm({
      title: plan.title || '',
      description: plan.description || '',
      goals: (plan.goals || []).join('\n'),
      frequency: plan.frequency || '',
      tasks
    });
    setIsCarePlanOpen(true);
  };

  const addCarePlanTask = () => {
    setCarePlanForm(prev => ({
      ...prev,
      tasks: [...prev.tasks, { name: '', notes: '', required: false, days_of_week: [] }]
    }));
  };

  const toggleLibraryActivity = (name) => {
    setCarePlanForm(prev => {
      const exists = prev.tasks.some(t => (t.name || '').trim().toLowerCase() === name.toLowerCase());
      if (exists) {
        return { ...prev, tasks: prev.tasks.filter(t => (t.name || '').trim().toLowerCase() !== name.toLowerCase()) };
      }
      return {
        ...prev,
        tasks: [...prev.tasks, { name, notes: '', required: false, days_of_week: [] }]
      };
    });
  };

  const updateCarePlanTask = (idx, patch) => {
    setCarePlanForm(prev => ({
      ...prev,
      tasks: prev.tasks.map((t, i) => i === idx ? { ...t, ...patch } : t)
    }));
  };

  const removeCarePlanTask = (idx) => {
    setCarePlanForm(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== idx)
    }));
  };

  const toggleCarePlanTaskDay = (idx, day) => {
    setCarePlanForm(prev => ({
      ...prev,
      tasks: prev.tasks.map((t, i) => {
        if (i !== idx) return t;
        const has = (t.days_of_week || []).includes(day);
        return {
          ...t,
          days_of_week: has
            ? t.days_of_week.filter(d => d !== day)
            : [...(t.days_of_week || []), day]
        };
      })
    }));
  };

  const handleCreateCarePlan = async (e) => {
    e.preventDefault();
    if (!carePlanForm.title.trim() || !carePlanForm.description.trim()) {
      toast.error('Title and description are required');
      return;
    }
    const cleanedTasks = (carePlanForm.tasks || [])
      .filter(t => (t.name || '').trim())
      .map(t => ({
        name: t.name.trim(),
        notes: (t.notes || '').trim(),
        required: !!t.required,
        days_of_week: Array.isArray(t.days_of_week) ? t.days_of_week : []
      }));
    setCarePlanSaving(true);
    try {
      const payload = {
        client_id: clientId,
        title: carePlanForm.title.trim(),
        description: carePlanForm.description.trim(),
        goals: carePlanForm.goals.split('\n').map(s => s.trim()).filter(Boolean),
        activities: cleanedTasks.map(t => t.name), // back-compat
        tasks: cleanedTasks,
        frequency: carePlanForm.frequency.trim() || null
      };
      if (editingCarePlanId) {
        await carePlansAPI.update(editingCarePlanId, payload);
        toast.success('Care plan updated');
      } else {
        await carePlansAPI.create(payload);
        toast.success('Care plan created');
      }
      setIsCarePlanOpen(false);
      setEditingCarePlanId(null);
      const plansRes = await carePlansAPI.getAll(clientId);
      setCarePlans(plansRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save care plan');
    } finally { setCarePlanSaving(false); }
  };

  const handleAISuggestCarePlan = async () => {
    setAiSuggesting(true);
    try {
      const response = await aiAPI.suggestCarePlan(clientId, '');
      const suggestion = response.data.suggestion || '';
      setCarePlanForm(prev => ({
        ...prev,
        title: prev.title || `AI Suggested Care Plan — ${client?.name || 'Client'}`,
        description: suggestion.substring(0, 1000)
      }));
      toast.success('AI suggestions generated');
    } catch (error) {
      toast.error('Failed to generate AI suggestion');
    } finally { setAiSuggesting(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      await filesAPI.upload(clientId, file);
      toast.success(`Uploaded: ${file.name}`);
      const filesRes = await filesAPI.list(clientId);
      setFiles(filesRes.data);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileDownload = async (fileRecord) => {
    try {
      const response = await filesAPI.download(fileRecord.id);
      const blob = new Blob([response.data], { type: fileRecord.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileRecord.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      await filesAPI.delete(fileId);
      toast.success('File deleted');
      setFiles(files.filter(f => f.id !== fileId));
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleCreateDocument = async (e) => {
    e.preventDefault();
    try {
      if (editingDoc) {
        await clientDocumentsAPI.update(editingDoc.id, { ...docForm, client_id: clientId });
        toast.success('Document updated');
      } else {
        await clientDocumentsAPI.create({ ...docForm, client_id: clientId });
        toast.success('Document created');
      }
      setIsDocCreateOpen(false);
      setEditingDoc(null);
      setDocForm({ title: '', doc_type: 'note', content: '' });
      const docsRes = await clientDocumentsAPI.list(clientId);
      setDocuments(docsRes.data);
    } catch (error) {
      toast.error(editingDoc ? 'Failed to update document' : 'Failed to create document');
    }
  };

  const handleEditDocument = (doc) => {
    setEditingDoc(doc);
    setDocForm({ title: doc.title, doc_type: doc.doc_type, content: doc.content });
    setIsDocCreateOpen(true);
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await clientDocumentsAPI.delete(docId);
      toast.success('Document deleted');
      setDocuments(documents.filter(d => d.id !== docId));
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return FilePdf;
    if (contentType?.includes('image')) return FileImage;
    if (contentType?.includes('word') || contentType?.includes('document')) return FileDoc;
    return FileIcon;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getDocTypeColor = (type) => {
    switch (type) {
      case 'note': return 'bg-blue-100 text-blue-800';
      case 'assessment': return 'bg-green-100 text-green-800';
      case 'incident_report': return 'bg-red-100 text-red-800';
      case 'care_note': return 'bg-purple-100 text-purple-800';
      case 'plan_update': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Client not found</p>
        <Button variant="outline" className="mt-4 rounded-xl" onClick={() => navigate('/clients')}>
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="client-profile-page">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" className="rounded-xl gap-1" onClick={() => navigate('/clients')} data-testid="back-to-clients-btn">
          <ArrowLeft weight="bold" className="w-4 h-4" />
          Back
        </Button>
      </div>

      {/* Profile Header Card */}
      <Card className="rounded-2xl border border-border" data-testid="client-profile-header">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
              <Users weight="duotone" className="w-10 h-10 text-primary" />
            </div>
            <div className="flex-1 space-y-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-semibold flex items-center gap-3">
                    {client.name}
                    {client.status === 'lead' && (
                      <Badge className="rounded-full text-xs bg-violet-100 text-violet-800" data-testid="lead-status-badge">Lead</Badge>
                    )}
                  </h1>
                  {client.date_of_birth && (
                    <p className="text-muted-foreground mt-1 flex items-center gap-2">
                      <Calendar weight="duotone" className="w-4 h-4" />
                      Born: {client.date_of_birth}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {(user?.role === 'supervisor' || user?.role === 'admin' || user?.role === 'super_admin') && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-xl gap-1"
                      onClick={openEditProfile}
                      data-testid="edit-client-profile-btn"
                    >
                      <PencilSimple weight="bold" className="w-3.5 h-3.5" /> Edit Profile
                    </Button>
                  )}
                  {client.status === 'lead' && (user?.role === 'supervisor' || user?.role === 'admin' || user?.role === 'super_admin') && (
                    <Button
                      className="rounded-xl gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={handleConvertToActive}
                      disabled={convertingLead}
                      data-testid="convert-to-active-btn"
                    >
                      <ShieldCheck weight="bold" className="w-4 h-4" />
                      {convertingLead ? 'Converting...' : 'Convert to Active'}
                    </Button>
                  )}
                  <span className="hipaa-badge">
                    <ShieldCheck weight="duotone" className="w-3 h-3" />
                    Protected Record
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {client.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin weight="duotone" className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">{client.address}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm" data-testid="client-profile-timezone">
                  <Calendar weight="duotone" className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">
                    <strong>Time zone:</strong>{' '}
                    {(US_TIMEZONES.find(t => t.value === (client.timezone || 'America/New_York'))?.label) || (client.timezone || 'America/New_York')}{' '}
                    <span className="text-xs uppercase font-mono">({tzAbbrev(undefined, client.timezone)})</span>
                  </span>
                  {(user?.role === 'supervisor' || user?.role === 'super_admin' || user?.role === 'admin') && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="rounded-full h-6 px-2 text-xs"
                      onClick={openTimezone}
                      data-testid="edit-timezone-btn"
                    >
                      <PencilSimple weight="bold" className="w-3 h-3" />
                    </Button>
                  )}
                </div>
                {client.email && (
                  <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="client-profile-email">
                    <EnvelopeSimple weight="duotone" className="w-4 h-4 text-muted-foreground shrink-0" />
                    <a href={`mailto:${client.email}`} className="text-primary hover:underline truncate">{client.email}</a>
                    {(user?.role === 'supervisor' || user?.role === 'super_admin' || user?.role === 'admin') && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="rounded-full text-xs h-7 gap-1 ml-auto"
                        onClick={openWelcome}
                        data-testid="send-welcome-email-btn"
                      >
                        <EnvelopeSimple weight="bold" className="w-3 h-3" />
                        {client.welcome_emailed_at ? 'Resend Welcome' : 'Send Welcome Email'}
                      </Button>
                    )}
                  </div>
                )}
                {!client.email && (user?.role === 'supervisor' || user?.role === 'super_admin' || user?.role === 'admin') && (
                  <div className="flex items-center gap-2 text-sm" data-testid="client-profile-no-email">
                    <EnvelopeSimple weight="duotone" className="w-4 h-4 text-muted-foreground/60 shrink-0" />
                    <span className="text-muted-foreground italic">No email on file</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full text-xs h-7 gap-1 ml-auto"
                      onClick={openWelcome}
                      data-testid="send-welcome-email-btn"
                    >
                      <EnvelopeSimple weight="bold" className="w-3 h-3" />
                      Send Welcome Email
                    </Button>
                  </div>
                )}
                {client.emergency_contact && (
                  <div className="flex items-start gap-2 text-sm">
                    <Phone weight="duotone" className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <span className="text-muted-foreground">
                      <strong>Emergency:</strong> {client.emergency_contact}
                      {client.emergency_phone && ` - ${client.emergency_phone}`}
                      {client.emergency_email && (
                        <>
                          {' · '}<a href={`mailto:${client.emergency_email}`} className="text-primary hover:underline">{client.emergency_email}</a>
                        </>
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-4">
                {client.medical_conditions?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <FirstAid weight="duotone" className="w-4 h-4 text-muted-foreground mt-1 shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {client.medical_conditions.map((c, i) => (
                        <Badge key={i} variant="secondary" className="text-xs rounded-full">{c}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {client.allergies?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Warning weight="duotone" className="w-4 h-4 text-destructive mt-1 shrink-0" />
                    <div className="flex flex-wrap gap-1">
                      {client.allergies.map((a, i) => (
                        <Badge key={i} className="text-xs rounded-full bg-destructive/10 text-destructive">{a}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {client.notes && (
                <p className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-xl italic">
                  {client.notes}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50 rounded-xl p-1">
          <TabsTrigger value="overview" className="rounded-lg" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="files" className="rounded-lg" data-testid="tab-files">
            Files ({files.length})
          </TabsTrigger>
          <TabsTrigger value="documents" className="rounded-lg" data-testid="tab-documents">
            Documents ({documents.length})
          </TabsTrigger>
          <TabsTrigger value="care-plans" className="rounded-lg" data-testid="tab-care-plans">
            Care Plans ({carePlans.length})
          </TabsTrigger>
          <TabsTrigger value="visits" className="rounded-lg" data-testid="tab-visits">
            Schedule
          </TabsTrigger>
          <TabsTrigger value="notes" className="rounded-lg" data-testid="tab-notes">
            Notes
          </TabsTrigger>
          <TabsTrigger value="forms" className="rounded-lg" data-testid="tab-forms">
            Forms
          </TabsTrigger>
          {(user?.role === 'supervisor' || user?.role === 'super_admin' || user?.role === 'admin') && (
            <TabsTrigger value="incidents" className="rounded-lg" data-testid="tab-incidents">
              Incidents
            </TabsTrigger>
          )}
          {(user?.role === 'supervisor' || user?.role === 'super_admin') && (
            <TabsTrigger value="billing" className="rounded-lg" data-testid="tab-billing">
              Billing
            </TabsTrigger>
          )}
        </TabsList>

        {/* OVERVIEW TAB */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="rounded-2xl border border-border">
              <CardContent className="p-6 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Uploaded Files</p>
                <h3 className="font-heading text-3xl font-semibold mt-2">{files.length}</h3>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-border">
              <CardContent className="p-6 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Documents</p>
                <h3 className="font-heading text-3xl font-semibold mt-2">{documents.length}</h3>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-border">
              <CardContent className="p-6 text-center">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Active Care Plans</p>
                <h3 className="font-heading text-3xl font-semibold mt-2">{carePlans.filter(p => p.is_active).length}</h3>
              </CardContent>
            </Card>
          </div>

          {/* Recent documents */}
          <Card className="rounded-2xl border border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Recent Documents</CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">No documents yet</p>
              ) : (
                <div className="space-y-3">
                  {documents.slice(0, 3).map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 rounded-xl bg-muted/30">
                      <div className="flex items-center gap-3">
                        <Note weight="duotone" className="w-5 h-5 text-primary" />
                        <div>
                          <p className="font-medium text-sm">{doc.title}</p>
                          <p className="text-xs text-muted-foreground">{format(new Date(doc.created_at), 'MMM d, yyyy')}</p>
                        </div>
                      </div>
                      <Badge className={`${getDocTypeColor(doc.doc_type)} rounded-full text-xs`}>
                        {doc.doc_type.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* FILES TAB */}
        <TabsContent value="files" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold">Uploaded Files</h2>
              <p className="text-sm text-muted-foreground">PDFs, images, and documents</p>
            </div>
            {(user?.role === 'supervisor' || user?.role === 'caregiver') && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.csv,.xlsx"
                  data-testid="file-upload-input"
                />
                <Button
                  className="rounded-xl gap-2"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="upload-file-btn"
                >
                  <UploadSimple weight="bold" className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload File'}
                </Button>
              </div>
            )}
          </div>

          {files.length === 0 ? (
            <Card className="rounded-2xl border border-border">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileIcon weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
                <h3 className="font-heading text-lg font-medium">No files uploaded</h3>
                <p className="text-muted-foreground text-sm mt-1">Upload documents, images, or PDFs</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {files.map(file => {
                const Icon = getFileIcon(file.content_type);
                return (
                  <Card key={file.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`file-card-${file.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <Icon weight="duotone" className="w-6 h-6 text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{file.original_filename}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatFileSize(file.size)} &middot; Uploaded by {file.uploaded_by_name} &middot; {format(new Date(file.created_at), 'MMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="rounded-xl"
                            onClick={() => handleFileDownload(file)}
                            data-testid={`download-file-${file.id}`}
                          >
                            <DownloadSimple weight="bold" className="w-4 h-4" />
                          </Button>
                          {user?.role === 'supervisor' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="rounded-xl text-destructive hover:text-destructive"
                              onClick={() => handleFileDelete(file.id)}
                              data-testid={`delete-file-${file.id}`}
                            >
                              <Trash weight="bold" className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* DOCUMENTS TAB */}
        <TabsContent value="documents" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold">Client Documents</h2>
              <p className="text-sm text-muted-foreground">Notes, assessments, and reports created in the system</p>
            </div>
            {(user?.role === 'supervisor' || user?.role === 'caregiver') && (
              <Dialog open={isDocCreateOpen} onOpenChange={(open) => { setIsDocCreateOpen(open); if (!open) { setEditingDoc(null); setDocForm({ title: '', doc_type: 'note', content: '' }); } }}>
                <DialogTrigger asChild>
                  <Button className="rounded-xl gap-2" data-testid="create-document-btn">
                    <Plus weight="bold" className="w-4 h-4" />
                    Create Document
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingDoc ? 'Edit Document' : 'Create Document'}</DialogTitle>
                    <DialogDescription>
                      {editingDoc ? 'Update the document content' : 'Create a new document for this client'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateDocument} className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Document Title *</Label>
                      <Input
                        value={docForm.title}
                        onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                        placeholder="Initial Assessment - Q1 2026"
                        required
                        className="rounded-xl"
                        data-testid="doc-title-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Document Type *</Label>
                      <Select value={docForm.doc_type} onValueChange={(value) => setDocForm({ ...docForm, doc_type: value })}>
                        <SelectTrigger className="rounded-xl" data-testid="doc-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="note">General Note</SelectItem>
                          <SelectItem value="assessment">Assessment</SelectItem>
                          <SelectItem value="care_note">Care Note</SelectItem>
                          <SelectItem value="incident_report">Incident Report</SelectItem>
                          <SelectItem value="plan_update">Plan Update</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Content *</Label>
                      <Textarea
                        value={docForm.content}
                        onChange={(e) => setDocForm({ ...docForm, content: e.target.value })}
                        placeholder="Enter document content..."
                        required
                        rows={10}
                        className="rounded-xl"
                        data-testid="doc-content-input"
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => { setIsDocCreateOpen(false); setEditingDoc(null); }}>
                        Cancel
                      </Button>
                      <Button type="submit" className="flex-1 rounded-xl" data-testid="save-document-btn">
                        {editingDoc ? 'Update' : 'Create'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {/* View Document Dialog */}
          <Dialog open={!!viewingDoc} onOpenChange={(open) => { if (!open) setViewingDoc(null); }}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
              {viewingDoc && (
                <>
                  <DialogHeader>
                    <div className="flex items-center justify-between">
                      <DialogTitle className="font-heading">{viewingDoc.title}</DialogTitle>
                      <Badge className={`${getDocTypeColor(viewingDoc.doc_type)} rounded-full text-xs`}>
                        {viewingDoc.doc_type.replace('_', ' ')}
                      </Badge>
                    </div>
                    <DialogDescription>
                      By {viewingDoc.created_by_name} &middot; {format(new Date(viewingDoc.created_at), 'MMM d, yyyy h:mm a')}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="mt-4 p-4 rounded-xl bg-muted/30 whitespace-pre-wrap text-sm" data-testid="doc-view-content">
                    {viewingDoc.content}
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {documents.length === 0 ? (
            <Card className="rounded-2xl border border-border">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Note weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
                <h3 className="font-heading text-lg font-medium">No documents yet</h3>
                <p className="text-muted-foreground text-sm mt-1">Create notes, assessments, or reports</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => (
                <Card key={doc.id} className="rounded-2xl border border-border hover:border-border/80 hover:shadow-sm transition-all duration-200" data-testid={`doc-card-${doc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                          <Note weight="duotone" className="w-6 h-6 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium text-sm">{doc.title}</p>
                            <Badge className={`${getDocTypeColor(doc.doc_type)} rounded-full text-xs`}>
                              {doc.doc_type.replace('_', ' ')}
                            </Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            By {doc.created_by_name} &middot; {format(new Date(doc.created_at), 'MMM d, yyyy h:mm a')}
                            {doc.updated_at !== doc.created_at && ' (edited)'}
                          </p>
                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {doc.content}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setViewingDoc(doc)} data-testid={`view-doc-${doc.id}`}>
                          <Eye weight="bold" className="w-4 h-4" />
                        </Button>
                        {(user?.role === 'supervisor' || user?.role === 'caregiver') && (
                          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => handleEditDocument(doc)} data-testid={`edit-doc-${doc.id}`}>
                            <PencilSimple weight="bold" className="w-4 h-4" />
                          </Button>
                        )}
                        {user?.role === 'supervisor' && (
                          <Button variant="ghost" size="sm" className="rounded-xl text-destructive hover:text-destructive" onClick={() => handleDeleteDocument(doc.id)} data-testid={`delete-doc-${doc.id}`}>
                            <Trash weight="bold" className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* CARE PLANS TAB */}
        <TabsContent value="care-plans" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold">Care Plans</h2>
              <p className="text-sm text-muted-foreground">Active care plans for this client</p>
            </div>
            {(user?.role === 'supervisor' || user?.role === 'super_admin') && (
              <Button className="rounded-xl gap-2" onClick={openCreateCarePlan} data-testid="create-care-plan-btn">
                <Plus weight="bold" className="w-4 h-4" />
                Create Care Plan
              </Button>
            )}
          </div>
          {carePlans.length === 0 ? (
            <Card className="rounded-2xl border border-border">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ClipboardText weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
                <h3 className="font-heading text-lg font-medium">No care plans yet</h3>
                <p className="text-muted-foreground text-sm mt-1">Click "Create Care Plan" to add one</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {carePlans.map(plan => {
                const displayTasks = Array.isArray(plan.tasks) && plan.tasks.length > 0
                  ? plan.tasks
                  : (plan.activities || []).map(a => ({ name: a }));
                return (
                <Card key={plan.id} className="rounded-2xl border border-border" data-testid={`profile-careplan-${plan.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-lg">{plan.title}</CardTitle>
                      <div className="flex items-center gap-2">
                        <Badge variant={plan.is_active ? "default" : "secondary"} className="rounded-full">
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        {(user?.role === 'supervisor' || user?.role === 'super_admin') && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-xl gap-1 h-8"
                            onClick={() => openEditCarePlan(plan)}
                            data-testid={`edit-careplan-${plan.id}`}
                          >
                            <PencilSimple weight="bold" className="w-3.5 h-3.5" />
                            Edit
                          </Button>
                        )}
                      </div>
                    </div>
                    {plan.frequency && <CardDescription>Frequency: {plan.frequency}</CardDescription>}
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-3 whitespace-pre-wrap">{plan.description}</p>
                    {plan.goals?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">Goals</p>
                        <div className="space-y-1">
                          {plan.goals.map((g, i) => (
                            <div key={i} className="text-sm flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                              {g}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {displayTasks.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2">Tasks <span className="normal-case text-muted-foreground/70 font-normal">(shown to caregiver at clock-out)</span></p>
                        <div className="space-y-1">
                          {displayTasks.map((t, i) => (
                            <div key={i} className="text-sm flex items-start gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500 mt-2 shrink-0" />
                              <div className="flex-1 min-w-0">
                                <span>{t.name}</span>
                                {t.required && <span className="ml-2 text-[10px] uppercase tracking-wider text-red-600 font-semibold">Required</span>}
                                {Array.isArray(t.days_of_week) && t.days_of_week.length > 0 && (
                                  <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                                    {t.days_of_week.map(d => d.slice(0,3)).join(', ')}
                                  </span>
                                )}
                                {t.notes && <p className="text-xs text-muted-foreground mt-0.5">{t.notes}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}

          {/* Create / Edit Care Plan Dialog */}
          <Dialog open={isCarePlanOpen} onOpenChange={(v) => { setIsCarePlanOpen(v); if (!v) setEditingCarePlanId(null); }}>
            <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editingCarePlanId ? 'Edit Care Plan' : 'Create Care Plan'}</DialogTitle>
                <DialogDescription>{editingCarePlanId ? `Update the care plan for ${client?.name}` : `Create a new care plan for ${client?.name}`}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCarePlan} className="space-y-4 mt-4">
                <div className="flex justify-end">
                  <Button type="button" variant="outline" className="rounded-xl gap-2" onClick={handleAISuggestCarePlan} disabled={aiSuggesting} data-testid="ai-suggest-care-plan-btn">
                    <ClipboardText weight="duotone" className="w-4 h-4" />
                    {aiSuggesting ? 'Generating...' : 'AI Suggest'}
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input value={carePlanForm.title} onChange={(e) => setCarePlanForm({ ...carePlanForm, title: e.target.value })} placeholder="e.g. Daily Living Assistance" className="rounded-xl" required data-testid="care-plan-title" />
                </div>
                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Textarea value={carePlanForm.description} onChange={(e) => setCarePlanForm({ ...carePlanForm, description: e.target.value })} placeholder="Overall care plan description..." className="rounded-xl min-h-[100px]" required data-testid="care-plan-description" />
                </div>
                <div className="space-y-2">
                  <Label>Goals <span className="text-xs text-muted-foreground">(one per line)</span></Label>
                  <Textarea value={carePlanForm.goals} onChange={(e) => setCarePlanForm({ ...carePlanForm, goals: e.target.value })} placeholder="Maintain mobility&#10;Medication adherence&#10;Social engagement" className="rounded-xl min-h-[80px]" data-testid="care-plan-goals" />
                </div>

                {/* Activity Library Picker */}
                <div className="space-y-2">
                  <Label>Activity Library <span className="text-xs text-muted-foreground">(tick which activities apply to this client)</span></Label>
                  <div className="rounded-xl border border-border bg-muted/20 overflow-hidden">
                    <Accordion type="multiple" className="w-full">
                      {ACTIVITY_LIBRARY.map((group, gi) => {
                        const selectedInGroup = group.items.filter(name =>
                          carePlanForm.tasks.some(t => (t.name || '').trim().toLowerCase() === name.toLowerCase())
                        ).length;
                        return (
                          <AccordionItem key={group.category} value={`cat-${gi}`} className="border-border">
                            <AccordionTrigger className="px-3 py-2 hover:no-underline" data-testid={`library-group-${gi}`}>
                              <div className="flex items-center gap-2 text-sm font-medium">
                                <span>{group.category}</span>
                                {selectedInGroup > 0 && (
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/15 text-primary">
                                    {selectedInGroup} selected
                                  </span>
                                )}
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-3">
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {group.items.map((name) => {
                                  const checked = carePlanForm.tasks.some(
                                    t => (t.name || '').trim().toLowerCase() === name.toLowerCase()
                                  );
                                  const testId = `library-item-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
                                  return (
                                    <label
                                      key={name}
                                      className={`flex items-start gap-2 rounded-lg border p-2 cursor-pointer select-none transition-colors ${
                                        checked ? 'bg-primary/5 border-primary/30' : 'bg-background border-border hover:bg-muted/40'
                                      }`}
                                      data-testid={testId}
                                    >
                                      <input
                                        type="checkbox"
                                        className="mt-0.5 rounded"
                                        checked={checked}
                                        onChange={() => toggleLibraryActivity(name)}
                                      />
                                      <span className="text-xs leading-snug">{name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  </div>
                </div>

                {/* Structured Tasks Editor */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Tasks for This Client <span className="text-xs text-muted-foreground">({carePlanForm.tasks.length} selected — shown to caregiver at clock-out)</span></Label>
                    <Button type="button" variant="outline" size="sm" className="rounded-xl gap-1 h-8" onClick={addCarePlanTask} data-testid="add-careplan-task-btn">
                      <Plus weight="bold" className="w-3.5 h-3.5" />
                      Custom Task
                    </Button>
                  </div>

                  {carePlanForm.tasks.length === 0 ? (
                    <div className="text-center py-6 text-sm text-muted-foreground rounded-xl bg-muted/30 border border-dashed border-border">
                      Tick activities from the Library above, or click "Custom Task" to add your own
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {carePlanForm.tasks.map((task, idx) => (
                        <div key={idx} className="rounded-xl border border-border p-3 space-y-2" data-testid={`careplan-task-row-${idx}`}>
                          <div className="flex items-start gap-2">
                            <div className="flex-1 space-y-2">
                              <Input
                                value={task.name}
                                onChange={(e) => updateCarePlanTask(idx, { name: e.target.value })}
                                placeholder="Task name (e.g. Bathing assistance)"
                                className="rounded-lg h-9"
                                data-testid={`careplan-task-name-${idx}`}
                              />
                              <Input
                                value={task.notes}
                                onChange={(e) => updateCarePlanTask(idx, { notes: e.target.value })}
                                placeholder="Instructions / notes (optional)..."
                                className="rounded-lg h-8 text-xs"
                                data-testid={`careplan-task-notes-${idx}`}
                              />
                            </div>
                            <Button type="button" variant="ghost" size="sm" className="rounded-xl h-8 px-2 text-destructive hover:text-destructive" onClick={() => removeCarePlanTask(idx)} data-testid={`remove-careplan-task-${idx}`}>
                              <Trash weight="bold" className="w-3.5 h-3.5" />
                            </Button>
                          </div>

                          <div className="flex items-center gap-4 flex-wrap pt-1">
                            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={task.required}
                                onChange={(e) => updateCarePlanTask(idx, { required: e.target.checked })}
                                className="rounded"
                                data-testid={`careplan-task-required-${idx}`}
                              />
                              <span className="font-medium">Required</span>
                              <span className="text-muted-foreground">(caregiver must complete)</span>
                            </label>
                          </div>

                          <div className="pt-1">
                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Days of week <span className="normal-case font-normal">(leave all off = every visit)</span></p>
                            <div className="flex gap-1 flex-wrap">
                              {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => {
                                const active = (task.days_of_week || []).includes(day);
                                return (
                                  <button
                                    type="button"
                                    key={day}
                                    onClick={() => toggleCarePlanTaskDay(idx, day)}
                                    className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'}`}
                                    data-testid={`careplan-task-day-${idx}-${day}`}
                                  >
                                    {day.slice(0,3).toUpperCase()}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Frequency <span className="text-xs text-muted-foreground">(overall plan description)</span></Label>
                  <Input value={carePlanForm.frequency} onChange={(e) => setCarePlanForm({ ...carePlanForm, frequency: e.target.value })} placeholder="e.g. Daily, 3x per week" className="rounded-xl" data-testid="care-plan-frequency" />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => { setIsCarePlanOpen(false); setEditingCarePlanId(null); }} disabled={carePlanSaving}>Cancel</Button>
                  <Button type="submit" className="flex-1 rounded-xl" disabled={carePlanSaving} data-testid="save-care-plan-btn">
                    {carePlanSaving ? 'Saving...' : (editingCarePlanId ? 'Save Changes' : 'Create Care Plan')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

        </TabsContent>

        {/* SCHEDULE TAB */}
        {/* NOTES TAB */}
        <TabsContent value="notes" className="space-y-6">
          <CareNotesFeed clientId={clientId} clients={client ? [client] : []} />
        </TabsContent>

        <TabsContent value="visits" className="space-y-6">
          <ClientScheduleTab
            clientId={clientId}
            clientTimezone={client?.timezone}
            visits={visits}
            userRole={user?.role}
            onRefresh={loadAllData}
          />
        </TabsContent>

        {/* FORMS TAB */}
        <TabsContent value="forms" className="space-y-6">
          <FormSubmissions entityType="client" entityId={clientId} userRole={user?.role} />
        </TabsContent>

        {/* INCIDENTS TAB */}
        {(user?.role === 'supervisor' || user?.role === 'super_admin' || user?.role === 'admin') && (
          <TabsContent value="incidents" className="space-y-6">
            <IncidentReportsTab
              clientId={clientId}
              clientName={client?.name}
              userRole={user?.role}
            />
          </TabsContent>
        )}

        {/* BILLING TAB */}
        <TabsContent value="billing" className="space-y-6">
          <ClientBilling clientId={clientId} clientData={client} userRole={user?.role} onRatesUpdated={loadAllData} />
        </TabsContent>
      </Tabs>

      {/* Edit Client Profile Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => { if (!open) setIsEditOpen(false); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Client Information</DialogTitle>
            <DialogDescription>Update the client's profile details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveProfile} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required className="rounded-xl" data-testid="edit-client-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input type="date" value={editForm.date_of_birth || ''} onChange={(e) => setEditForm({ ...editForm, date_of_birth: e.target.value })} className="rounded-xl" data-testid="edit-client-dob-input" />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={editForm.start_date || ''} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} className="rounded-xl" data-testid="edit-client-start-date-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea value={editForm.address || ''} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="rounded-xl" data-testid="edit-client-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Email <span className="text-xs text-muted-foreground">(used for invoice delivery)</span></Label>
              <Input type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="client@example.com" className="rounded-xl" data-testid="edit-client-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emergency Contact</Label>
                <Input value={editForm.emergency_contact || ''} onChange={(e) => setEditForm({ ...editForm, emergency_contact: e.target.value })} className="rounded-xl" data-testid="edit-client-emergency-contact-input" />
              </div>
              <div className="space-y-2">
                <Label>Emergency Phone</Label>
                <Input value={editForm.emergency_phone || ''} onChange={(e) => setEditForm({ ...editForm, emergency_phone: e.target.value })} className="rounded-xl" data-testid="edit-client-emergency-phone-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Emergency Contact Email</Label>
              <Input type="email" value={editForm.emergency_email || ''} onChange={(e) => setEditForm({ ...editForm, emergency_email: e.target.value })} className="rounded-xl" data-testid="edit-client-emergency-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Medical Conditions (comma-separated)</Label>
              <Textarea value={editForm.medical_conditions || ''} onChange={(e) => setEditForm({ ...editForm, medical_conditions: e.target.value })} className="rounded-xl" data-testid="edit-client-conditions-input" />
            </div>
            <div className="space-y-2">
              <Label>Allergies (comma-separated)</Label>
              <Input value={editForm.allergies || ''} onChange={(e) => setEditForm({ ...editForm, allergies: e.target.value })} className="rounded-xl" data-testid="edit-client-allergies-input" />
            </div>
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="rounded-xl" data-testid="edit-client-notes-input" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsEditOpen(false)} data-testid="edit-client-cancel">Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl" disabled={editSaving} data-testid="edit-client-save">
                {editSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Timezone Edit Dialog */}
      <Dialog open={isTzOpen} onOpenChange={(open) => { if (!open) setIsTzOpen(false); }}>
        <DialogContent className="sm:max-w-md" data-testid="timezone-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Client Time Zone</DialogTitle>
            <DialogDescription>
              Clock-in/out times for this client's visits are displayed and edited in this time zone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Time zone</Label>
              <Select value={tzDraft} onValueChange={setTzDraft}>
                <SelectTrigger className="rounded-xl" data-testid="timezone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {US_TIMEZONES.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Current local time: {new Date().toLocaleString('en-US', { timeZone: tzDraft, hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' })}</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setIsTzOpen(false)} disabled={tzSaving}>Cancel</Button>
              <Button className="flex-1 rounded-xl" onClick={handleSaveTimezone} disabled={tzSaving} data-testid="save-timezone-btn">
                {tzSaving ? 'Saving...' : 'Save Time Zone'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Welcome Email Dialog */}
      <Dialog open={isWelcomeOpen} onOpenChange={setIsWelcomeOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Send Welcome Email</DialogTitle>
            <DialogDescription>
              {client?.welcome_emailed_at
                ? `Previously sent ${format(new Date(client.welcome_emailed_at), 'PP')}. Resend or send to a different address.`
                : `Send a warm welcome and intro to ${client?.name}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Recipient email *</Label>
              <Input
                type="email"
                value={welcomeEmail}
                onChange={(e) => setWelcomeEmail(e.target.value)}
                placeholder="client@example.com"
                className="rounded-xl"
                data-testid="welcome-recipient-input"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">Defaults to the client's email; you can override for this send.</p>
            </div>
            <div className="space-y-2">
              <Label>Personal note <span className="text-xs text-muted-foreground">(optional)</span></Label>
              <Textarea
                value={welcomeMsg}
                onChange={(e) => setWelcomeMsg(e.target.value)}
                placeholder="e.g. 'Your first caregiver Jane will arrive Monday at 9am. Looking forward to meeting you!'"
                className="rounded-xl min-h-[90px]"
                data-testid="welcome-custom-message"
              />
            </div>
            <div className="rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground">
              <p><strong>Includes:</strong> a warm intro, what to expect at the first visit, how the care-plan check-ins work, and your contact info for questions.</p>
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setIsWelcomeOpen(false)} disabled={welcomeSending}>Cancel</Button>
              <Button className="flex-1 rounded-xl gap-2" onClick={handleSendWelcome} disabled={welcomeSending} data-testid="confirm-welcome-btn">
                <EnvelopeSimple weight="bold" className="w-4 h-4" />
                {welcomeSending ? 'Sending...' : 'Send Welcome'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientProfilePage;
